<template>
  <div class="row">
    <div class="col-sm-12 col-md-6 p-2">
      <v-calendar
        class="compact-calendar"
        ref="events-calendar"
        color="red"
        :min-date="minimumDate"
        :attributes="calendarOptions"
        @did-move="calendarPageNavigated"
        @dayclick="calendarDateClicked"
        :locale="getNavigatorLanguage"
      >
        <template v-slot:header-left-button>
          <div class="calendar-navigation-btn">
            <i class="far fa-long-arrow-left"></i>
          </div>
        </template>
        <template v-slot:header-right-button>
          <div class="calendar-navigation-btn">
            <i class="far fa-long-arrow-right"></i>
          </div>
        </template>
      </v-calendar>
    </div>
    <div class="col-sm-12 col-md-6 p-2">
      <div
        class="d-flex flex-column align-items-center justify-content-between h-100"
      >
        <div class="w-100">
          <div
            class="mb-2 d-flex flex-row justify-content-between align-items-center"
          >
            <span class="c-charcoal">{{
              $formatDate(
                selectedDate,
                'date-word-long',
                $t('PRODUCTS.LABELS.SELECT_A_DATE')
              )
            }}</span>
            <span>
              <i class="fa fa-clock-o me-1"></i>
              {{ duration }}{{ $t('GENERAL.LABEL.MINUTES_ABBREVIATION') }}
            </span>
          </div>
          <div class="mb-3">
            <v-select
              :options="timezones"
              label="name"
              code="value"
              v-model="selectedTimezone"
              :reduce="r => r.value"
              @update:modelValue="loadData"
            >
            </v-select>
          </div>
          <div
            class="d-flex flex-row flex-wrap justify-content-center justify-content-md-between"
          >
            <div
              class="day-slot rounded bg-brand-color c-white text-center px-3 py-1 mb-2 mx-2 mx-md-0 pointer"
              :class="{
                selected: selectedSlot === slot,
                readonly: readOnly,
                pointer: !readOnly,
              }"
              v-for="(slot, index) in selectedDailySlots"
              :key="index"
              @click="() => (selectedSlot = slot)"
            >
              {{ slot.format('LT') }}
            </div>
          </div>
        </div>
        <div class="w-100 mt-4">
          <HMButton
            v-if="selectedSlot && !readOnly"
            class="w-100 text-center"
            color="brand"
            size="md"
            @click="bookSlot"
            :disabled="isBooking"
            :loading="isBooking"
          >
            <span class="text-center w-100">
              {{
                $t('PRODUCTS.BUTTON.BOOK_SLOT', {
                  slot: $formatDate(selectedSlot, 'date-time'),
                })
              }}
            </span>
          </HMButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import moment from 'moment-timezone';
import { errorToMessage } from '@/utils/errors';
import colors from '@/styles/colors.scss?inline';
import { getNavigatorLanguage } from '@/utils/languages';
import timezones from '@/utils/timezones';
import _ from 'lodash-es';
import { useStudioStore, useProductStore } from '@/stores';
import { mapState } from 'pinia';
import  {toRaw} from 'vue';

export default {
  name: 'PrivateSchedulingCalendar',
  components: {},
  props: {
    studioURL: {
      type: String,
    },
    productId: {
      type: Number,
    },
    duration: {
      type: Number,
    },
    openInNewWindow: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState(useStudioStore, {
      studio: 'currentStudio',
    }),
    timezones() {
      return timezones;
    },
    calendarOptions() {
      return [...this.calendarOptionsConfig, this.dottedDays];
    },
    selectedDailySlots() {
      if (this.isLoading) return [];
      const selectedDateSlots = this.timeSlots.filter(slot =>
        slot.isSame(this.selectedDate, 'day')
      );
      return selectedDateSlots;
    },
    getNavigatorLanguage,
  },
  data() {
    return {
      selectedCalendarPage: {
        month: new Date().getMonth(),
        year: new Date().getFullYear(),
      },
      selectedDate: new Date(),
      minimumDate: new Date(),
      selectedSlot: null,
      selectedTimezone: null,
      dottedDays: {},
      calendarOptionsConfig: [
        {
          key: 'today',
          highlight: {
            class: 'todays-date-background',
            contentClass: 'todays-date-content',
          },
          dates: new Date(),
        },
      ],
      daySelected: false,
      isLoading: true,
      isBooking: false,
      timeSlots: null,
    };
  },
  methods: {
    async calendarPageNavigated(page) {
      this.selectedCalendarPage = toRaw(page)[0];
      if (!this.selectedTimezone) return;
      this.selectedSlot = null;
      await this.loadData();
      this.daySelected = true;
    },
    calendarDateClicked(day) {
      console.log('day', day);
      this.selectedSlot = null;
      this.selectedDate = moment(day.date).toDate();
      this.daySelected = true;
    },
    async loadData(initial = false) {
      this.isLoading = true;
      const y = this.selectedCalendarPage.year;
      const m = this.selectedCalendarPage.month - 1;
      const firstDay = moment(new Date(y, m, 1)).tz(this.selectedTimezone);
      const lastDay = moment(new Date(y, m + 1, 0))
        .add(1, 'days')
        .tz(this.selectedTimezone);
      try {
        let timeSlots = await useProductStore().getPrivateSchedulingTimeSlots({
          studioURL: this.studioURL,
          productId: this.productId,
          startDate: firstDay,
          endDate: lastDay,
          timezone: this.selectedTimezone,
        });
        if (initial && !timeSlots.length) {
          // if nothing is found in the first month, try the next 2 months
          const extendedLastDay = moment(new Date(y, m + 3, 0))
            .add('1, days')
            .tz(this.selectedTimezone);
          timeSlots = await useProductStore().getPrivateSchedulingTimeSlots({
              studioURL: this.studioURL,
              productId: this.productId,
              startDate: lastDay,
              endDate: extendedLastDay,
              timezone: this.selectedTimezone,
            }
          );
        }
        // map timeslots to moment objects and remove any past slots
        const now = moment().tz(this.selectedTimezone);
        this.timeSlots = timeSlots
          .map(slot => moment(slot).tz(this.selectedTimezone))
          .filter(slot => slot.isSameOrAfter(now));

        let dottedDays = [];
        this.timeSlots.forEach(slot => {
          slot = slot.clone();
          if (slot.isSameOrAfter(now, 'day')) {
            if (!dottedDays.includes(slot.startOf('day').toString())) {
              dottedDays.push(slot.startOf('day').toString());
            }
          }
          if (!this.minimumDate || slot.isBefore(this.minimumDate)) {
            this.minimumDate = slot.toDate();
          }
        });
        dottedDays = dottedDays.map(day => new Date(day));

        if (dottedDays.length) {
          this.selectedDate = dottedDays[0];
          this.$refs['events-calendar'].focusDate(this.selectedDate);
        } else {
          this.selectedDate = new Date();
        }

        this.dottedDays = {
          dot: {
            style: {
              backgroundColor: this.studio?.primary_color || colors.coral,
            },
          },
          dates: dottedDays,
        };
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to load available time slots',
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      } finally {
        this.isLoading = false;
      }
      if (initial) {
        this.$refs['events-calendar'].focusDate(this.selectedDate);
      }
    },
    async bookSlot() {
      this.isBooking = true;
      try {
        await useProductStore().bookPrivateSchedulingSlot({
          studioURL: this.studioURL,
          productId: this.productId,
          slot: this.selectedSlot,
          timezone: this.selectedTimezone,
        });
        this.loadData();
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t(
            'PRODUCTS.NOTIFICATIONS.PRIVATE_SESSION_SUCCESSFULLY_BOOKED_ON',
            {
              slot: this.$formatDate(
                this.selectedSlot.toDate(),
                'date-time-long'
              ),
            }
          ),
        });
        this.$emit('booked', this.selectedSlot);
        this.selectedSlot = null;
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to book your slot!',
          text: errorToMessage(error.response),
        });
      } finally {
        this.isBooking = false;
      }
    },
  },
  mounted() {
    this.selectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    this.loadData(true);
  },
};
</script>

<style lang="scss">
@import '@/styles/components-variables.scss';

.vc-light.vc-attr, .vc-light .vc-attr  {
  --vc-highlight-solid-bg: #{$charcoal};
}
</style>

<style lang="scss" scoped>
@import '@/styles/components-variables.scss';

#compact-calendar {
  --vc-border: transparent;
  --vc-header-title-color: #{$charcoal};
  --vc-weekday-color: #{$dark-gray};
  --vc-day-content-disabled-color: #{$lightest-gray};
  --vc-color: #{$medium-gray};
  --vc-dot-bg: blue;
  font-family: var(--brand-font, 'Poppins');
}

:deep(.vc-day)  {
  padding: 0.75rem 0;
  min-width: 28px;
}

// :deep(.todays-date-background) {
//   background-color: $charcoal;
//   border-radius: 50%;
//   box-sizing: content-box;
// }

// :deep(.todays-date-content)  {
//   color: $white;
// }

// :deep(.vc-day-content)  {
//   padding: 1rem;
// }

// /* move dot a bit lower*/
// :deep(.selectedDailyEvents)  {
//   height: 38px;
// }

// :deep(.vc-dots)  {
//   height: 0;
// }

// /* move buttons away from borders */
// :deep(.vc-arrows-container)  {
//   padding-top: 12px;
//   padding-left: 36px;
//   padding-right: 36px;

//   @media screen and (max-width: 992px) {
//     padding-left: 20px;
//     padding-right: 20px;
//   }
// }

:deep(.vc-arrow)  {
  background: transparent;
  width: 42px;
  &:hover {
    background-color: white;
  }
}

:deep(.vc-light.vc-attr, .vc-light .vc-attr)  {
  --vc-content-color: blue;
}

// :deep(.vc-day-box-center-bottom)  {
//   height: 40px;
// }

:deep(.vc-container)  {
  width: 100%;
  max-width: 100%;
  border: none;
  padding: 10px 15px;
  color: $dark-gray;
}

:deep(.vc-day-content)  {
  &:hover {
    background-color: $dark-gray !important;
    color: $white !important;
  }
  &:focus {
    background-color: var(--brand-color, $coral) !important;
    color: $white !important;
  }
}

:deep(.vc-header)  {
  height: auto;
  margin-bottom: 1.75rem;
  padding-left: 30px;
  padding-right: 30px;
}

// :deep(.vc-grid-cell)  {
//   padding: 0.5rem;
// }

:deep(.vc-title)  {
  background: transparent;
  color: $dark-gray !important;
  border-radius: 6px;
  border: 1px solid $medium-gray;
  padding: 5px 10px !important;
  font-family: var(--brand-font, 'Poppins');

  
  &:before {
    color: var(--brand-color, $coral);
    content: '';
    font-family: 'Font Awesome 5 Pro';
    font-weight: 400;
    display: inline-block;
    margin-right: 5px;
  }
}

// :deep(.vc-nav-popover-container)  {
//   background-color: $charcoal;
// }

.calendar-navigation-btn {
  margin-top: 8px;
  border-radius: 6px;
  border: 1px solid $medium-gray;
  padding: 6px 10px;
  width: 42px;

  &:hover {
    color: var(--brand-color, $coral);
    border-color: var(--brand-color, $coral);
    box-shadow: 0px 0px 1px 1px var(--brand-color, $coral);
    background-color: $white;
  }
}


.day-slot {
  min-width: 120px;
  animation: fliparound 0.5s ease-out;
  border: 1px solid var(--brand-color, $coral) !important;
  border-radius: $border-radius;
  background-color: white !important;
  color: var(--brand-color, $coral) !important;

  &.selected {
    color: white !important;
    background-color: var(--brand-color, $coral) !important;
  }

  &:hover {
    color: white !important;
    background-color: var(--brand-color, $coral) !important;
  }

  &.readonly {
    pointer-events: none;
    &:hover {
      color: var(--brand-color, $coral) !important;
      background-color: white !important;
    }
  }
}

@keyframes fliparound {
  0% {
    transform: rotateX(-120deg);
  }
  100% {
    transform: rotateX(0deg);
  }
}
</style>

